import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Search, Zap, Ban, DollarSign, ArrowRight } from "lucide-react";
import { useRef, useState } from "react";

export default function OurFeatures() {
  const { ref, isVisible } = useScrollReveal();
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const scrollContainerRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  const features = [
    {
      icon: Search,
      title: "Auto Detection",
      description: "We scan YouTube 24/7 to identify reuploads and matching videos, including partial copies of your content.",
    },
    {
      icon: Zap,
      title: "Instant Claims",
      description: "Content ID claims are automatically filed when matching uploads are detected.",
    },
    {
      icon: Ban,
      title: "Strike Management",
      description: "When necessary, copyright strikes are issued to remove repeat infringements.",
    },
    {
      icon: DollarSign,
      title: "Revenue Recovery",
      description: "Turn stolen views into revenue by monetizing eligible reuploads.",
    },
  ];

  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white py-20 lg:py-32 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-12 4xl:px-16`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Label */}
        <p className="text-[#757575] text-[18px] 4xl:text-[24px] font-medium text-left mb-6">
          Our Features
        </p>

        {/* Main Heading */}
        <h1 className="text-5xl xxs:text-[55px] first-letter:lg:text-[80px] 4xl:text-[80px] font-normal text-black text-left leading-[106px] xxs:leading-normal xs:leading-normal sm:leading-normal md:leading-normal lg:leading-normal mb-6 enfinity-header-font" style={{ lineHeight: "1.2" }}>
          <span className="text-[#8b5cf6]">Protect</span> your content. <br/>
          <span className="text-[#8b5cf6]">Reclaim</span> what's yours.
        </h1>

        {/* Subtitle + Arrow CTA */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <p className="text-xl xxs:text-[1rem] lg:text-[24px] font-normal text-black text-left leading-[34px] max-w-3xl enfinity-subtext-font whitespace-normal flex-1 min-w-0">
            Everything you need to protect your content, manage your rights, and monetize eligible reuploads in one platform.
          </p>
          <button
            type="button"
            onClick={scrollContainerRight}
            className="hidden md:flex flex-shrink-0 w-14 h-14 md:w-16 md:h-9 rounded-full bg-[#8b5cf6] items-center justify-center text-white shadow-lg cursor-pointer hover:bg-[#7336ff] transition-colors"
            aria-label="Scroll content to the right"
          >
            <ArrowRight className="w-7 h-7 md:w-6 md:h-6" strokeWidth={2.5} />
          </button>
        </div>

        {/* Scrollable Features Container */}
        <div className="relative -mx-2 xxs:-mx-1 xs:-mx-1 sm:-mx-1 md:-mx-4 lg:-mx-6 xl:-mx-8">
          <div
            ref={scrollContainerRef}
            className="relative overflow-x-auto pb-8 cursor-grab active:cursor-grabbing select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={(e) => {
              setIsDragging(true);
              setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
              setScrollLeft(scrollContainerRef.current.scrollLeft);
            }}
            onMouseLeave={() => setIsDragging(false)}
            onMouseUp={() => setIsDragging(false)}
            onMouseMove={(e) => {
              if (!isDragging) return;
              e.preventDefault();
              const x = e.pageX - scrollContainerRef.current.offsetLeft;
              const walk = (x - startX) * 2;
              scrollContainerRef.current.scrollLeft = scrollLeft - walk;
            }}
          >
            <div className="flex gap-4 xxs:gap-4 xs:gap-5 sm:gap-6 md:gap-6 lg:gap-[30px] px-2 xxs:px-2 xs:px-3 sm:px-3 md:px-4 lg:px-6 xl:px-8 min-w-max enfinity-subtext-font">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.1)] w-[280px] xxs:w-[300px] xs:w-[320px] sm:w-[380px] md:w-[420px] lg:w-[480px] h-[300px] xxs:h-[280px] xs:h-[300px] sm:h-[300px] md:h-[320px] lg:h-[345px] flex-shrink-0 p-4 xxs:p-4 xs:p-5 sm:p-6 md:p-8 lg:p-[39px] flex flex-col enfinity-subtext-font"
                  >
                    {/* Icon Box */}
                    <div className="bg-gradient-to-b from-[#8b5cf6] to-[#7336ff] rounded-[20px] w-[70px] h-[70px] xxs:w-[70px] xxs:h-[70px] xs:w-[75px] xs:h-[75px] sm:w-[75px] sm:h-[75px] md:w-[80px] md:h-[80px] lg:w-[80px] lg:h-[80px] flex items-center justify-center mb-4 xxs:mb-4 xs:mb-5 sm:mb-6 md:mb-8 lg:mb-[38px]">
                      <Icon className="w-7 h-7 xxs:w-7 xxs:h-7 xs:w-8 xs:h-8 sm:w-9 sm:h-9 md:w-9 md:h-9 lg:w-10 lg:h-10 text-white" strokeWidth={1} />
                    </div>

                    {/* Title */}
                    <h3 className="text-[22px] xxs:text-[22px] xs:text-[24px] sm:text-[26px] md:text-[28px] lg:text-[32px] font-medium text-black mb-3 xxs:mb-3 xs:mb-3 sm:mb-3 md:mb-4 lg:mb-4 leading-normal enfinity-header-font">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[16px] xxs:text-[1rem] lg:text-[20px] font-normal text-black leading-[24px] xxs:leading-loose xs:leading-loose sm:leading-loose md:leading-[30px] lg:leading-[32px] enfinity-subtext-font">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

